/**
 * Utilities for handling organization-specific routes
 */

type OrgRouteParams = {
  orgSlug?: string;
  path?: string;
};

/**
 * Generates an organization-specific URL path
 * 
 * @param params The route parameters
 * @returns The organization-specific URL path
 */
export const getOrgPath = (params: OrgRouteParams): string => {
  const { orgSlug, path = '/' } = params;

  if (!orgSlug) {
    return path;
  }

  // If path starts with a slash, remove it to avoid double slashes
  const normalizedPath = path.startsWith('/') ? path.substring(1) : path;

  return `/org/${orgSlug}/${normalizedPath}`;
};

/**
 * Check if a given URL path is an organization-specific path
 * 
 * @param path The URL path to check
 * @returns True if the path is organization-specific
 */
export const isOrgPath = (path: string): boolean => {
  // Check if path starts with /org/ followed by something
  if (path === '/' || path === '') return false;

  // Check if it matches /org/<slug> pattern
  return /^\/org\/[^\/]+/.test(path);
};

/**
 * Extract the organization slug from a path
 * 
 * @param path The URL path
 * @returns The organization slug, or undefined if not found
 */
export const extractOrgSlug = (path: string): string | undefined => {
  if (!isOrgPath(path)) {
    return undefined;
  }

  // Extract slug from /org/<slug>/* pattern
  const match = path.match(/^\/org\/([^\/]+)/);
  return match?.[1] || undefined;
}; 